/**
 * Open an OAuth window, returning a promise that resolves with the result
 * of the OAuth process.
 */
export function openOAuthWindow({
  authoriseUrl,
  windowName,
  width,
  height,
}: {
  authoriseUrl: string;
  windowName: string;
  width: number;
  height: number;
}): {
  window: Window;
  result: Promise<OAuthResult>;
} {
  const screenLeft =
    window.screenLeft !== undefined ? window.screenLeft : window.screenX;

  const screenTop =
    window.screenTop !== undefined ? window.screenTop : window.screenY;

  const screenWidth =
    window.innerWidth || document.documentElement.clientWidth || screen.width;

  const screenHeight =
    window.innerHeight ||
    document.documentElement.clientHeight ||
    screen.height;

  const left = screenLeft + (screenWidth - width) / 2;
  const top = screenTop + (screenHeight - height) / 2;

  const oauthWindow = window.open(
    authoriseUrl,
    windowName,
    `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, copyhistory=no` +
      `, width=${width}, height=${height}, top=${top}, left=${left}`,
  );

  if (!oauthWindow) {
    throw new WindowOpenFailedError();
  }

  const result = new Promise<OAuthResult>((resolve) => {
    let resolved = false;

    const finish = (result: OAuthResult) => {
      if (resolved) {
        return;
      }

      resolved = true;

      window.removeEventListener("message", handleMessage);
      window.clearInterval(windowClosedTimer);

      resolve(result);
    };

    const handleMessage = (event: MessageEvent<AuthoriseWindowEventData>) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.status === "success") {
        finish({ status: "success" });
        return;
      }

      if (event.data.status === "error") {
        finish({
          status: "error",
          error: new Error(
            event.data.errorReason ?? "Something went wrong. Please try again.",
          ),
        });
        return;
      }

      if (event.data.status === "denied") {
        finish({ status: "denied" });
        return;
      }
    };

    window.addEventListener("message", handleMessage);

    // check if the window was manually closed at regular intervals
    const windowClosedTimer = window.setInterval(() => {
      if (oauthWindow.closed) {
        finish({ status: "closed" });
      }
    }, 100);
  });

  return {
    window: oauthWindow,
    result,
  };
}

export class WindowOpenFailedError extends Error {
  constructor() {
    super("Unable to open authorisation window.");
    this.name = "WindowOpenFailedError";
  }
}
export type OAuthResult =
  | { status: "success" }
  | { status: "denied" }
  | { status: "error"; error: Error }
  | { status: "closed" };

export type AuthoriseWindowEventData = {
  status: "success" | "denied" | "error";
  errorReason?: string;
};
